import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, useDispatch, useSelector } from "react-redux";
import { createEgressProviderMapping, deleteEgressProviderMapping, getEgressGateways, getEgressProviderMappings, getEgressProviders, updateEgressProviderMapping } from "../../actions/actionEgressProvider";
import DataTable from "../../components/Table/Table";
import { Modal, ModalFooter } from "../Form/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowProgress } from "@fortawesome/pro-light-svg-icons/faArrowProgress";
import { FormField, Groups, notifyFailure, notifySuccess } from "../Form/Form";
import { Field, formValueSelector, reduxForm, reset } from "redux-form";
import { renderField, renderPass, renderSelectField, renderToggle, required } from "../../utils/formValidations";
import LoadingSpinner from "../LoadingSpinner";
import { ConfirmAction } from "../Table/NewTable";
import { faTrash } from "@fortawesome/pro-light-svg-icons/faTrash";
import SelectInput from "../SelectInput/SelectInput";

const selector = formValueSelector('EgressMapping')

function EgressMapping(props) {

  const { handleSubmit } = props
  const { t } = useTranslation('common');
  const dispatch = useDispatch()
  const { allow_all_gateways, egress_provider_id, selected_gateways, egress_provider_mapping_id } = props
  const [modal, setModal] = useState(false)
  const [edit, setEdit] = useState(false)
  const [confirmModal, setConfirmModal] = useState(false)
  const [mappings, setMappings] = useState(null)
  const [mapping, setMapping] = useState(null)
  const [gateways, setGateways] = useState(null)
  const [egressProviderOptions, setEgressProviderOptions] = useState(null)
  const [selectedGateways, setSelectedGateways] = useState([])
  const [selectedEgressProvider, setSelectedEgressProvider] = useState(null)
  const egressMappings = useSelector(state => state.egress_provider.egressMappings) || null
  const egress_providers = useSelector(state => state.egress_provider.egress_providers) || null
  const egressMappingsLoading = useSelector(state => state.egress_provider.egressMappingsLoading) || null
  const egressGateways = useSelector(state => state.egress_provider.egress_gateways) || null

  const getMappings = () => {
    return getEgressProviderMappings({
      type: props.type,
      id: props.source_id.replaceAll('-', '')
    })
  }

  useEffect(() => {
    dispatch(getMappings())
    dispatch(getEgressProviders())
    dispatch(getEgressGateways())
  }, []);

  useEffect(() => {
    if (egressMappings !== null) {
      setMappings(egressMappings.filter(mapping => mapping[props.type] === props.source_id.replaceAll('-', '')))
    }
  }, [egressMappings]);

  useEffect(() => {
    // Set the egress provider options given to the admin. Ensure that a user map an egress provider twice to the same resource.
    if (egress_providers == null || mappings == null) {
      return
    }
    let provider_options = []
    egress_providers.forEach((egress_provider) => {
      let provider_exists = false
      mappings.forEach((egress_provider_mapping) => {
        if (egress_provider_mapping.egress_provider_id === egress_provider.egress_provider_id) {
          provider_exists = true
        }
      });
      if (edit === true && egress_provider.egress_provider_id === selectedEgressProvider.egress_provider_id) {
        // For instances where are updating a mapping, we want to ensure that the already selected provider exists as an option
        provider_options.push(egress_provider)
      } else if (provider_exists === false) {
        provider_options.push(egress_provider)
      }
    });
    setEgressProviderOptions(provider_options)
  }, [egress_providers, mappings, edit, selectedEgressProvider])

  useEffect(() => {
    if (modal === false) {
      props.change('created', null)
      props.change('egress_provider_id', null)
      props.change('egress_provider_mapping_id', null)
      props.change('selected_gateways', null)
      props.change('enabled', true)
      props.change('allow_all_gateways', true)
      setSelectedGateways([])
    }
  }, [modal]);

  useEffect(() => {
    if (currentProvider && egressGateways !== null) {
      let optionsGateways = [];
      egressGateways.map(opt => {
        if (opt.egress_provider_id === currentProvider.egress_provider_id) {
          optionsGateways.push({ label: opt.name, value: opt.egress_gateway_id });
        }
      });
      optionsGateways.sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase()) ? 1 : -1);
      setGateways(optionsGateways)
    }
  }, [egress_provider_id]);

  const currentProvider = egress_provider_id && egressProviderOptions && egressProviderOptions.find(provider => provider.egress_provider_id === egress_provider_id)

  const deleteMapping = async (setMapping = false) => {
    const egress_provider_mapping_id = setMapping || mapping.egress_provider_mapping_id
    let data = { egress_provider_mapping_id }
    try {
      const { response: { error_message: errorMessage } } = await dispatch(deleteEgressProviderMapping(data));
      notifySuccess({ errorMessage, type: 'delete' })
      if (!setMapping) {
        dispatch(getMappings())
      }
    } catch (error) {
      notifyFailure({ error, type: 'delete' })
    }
    setConfirmModal(false)
  }

  const tableColumns = [
    {
      type: "text",
      name: t('egress_provider.egress-provider-id'),
      accessor: "egress_provider.name",
    },
    {
      type: "flag",
      name: t('egress_provider.enabled'),
      accessor: "enabled",
    },
    {
      type: "flag",
      name: t('egress_provider.allow-all-gateways'),
      accessor: "allow_all_gateways",
    },
    {
      type: "text",
      name: t('egress_provider.egress-provider-mapping-id'),
      accessor: "egress_provider_mapping_id",
      showByDefault: false,
    },
  ];

  const actions = [
    { id: "edit", icon: "fa-pencil", description: t('buttons.Edit') },
    { id: "delete", icon: "fa-trash", description: t('buttons.Delete') },
  ];

  const onAction = async (action, item) => {
    switch (action) {
      case "edit":
        updateMapping(item)
        setMapping(item)
        break;

      case "delete":
        setMapping(item)
        setConfirmModal(true)
        break;
      case "deleteMulti":
        const promises = []
        item.forEach(id => {
          const promise = deleteMapping(id)
          promises.push(promise)
        })
        await Promise.all(promises)
        dispatch(getMappings())
        break;
    }
  }

  const updateMapping = (item) => {
    setEdit(true)
    setSelectedEgressProvider(item)
    setSelectedGateways(item.egress_gateways.map(gateway => gateway.egress_gateway_id))
    props.initialize({
      ...item
    })
    setModal(true)
  }

  const addMapping = async (userData) => {
    const type = userData.egress_provider_mapping_id ? 'update' : 'create'
    try {
      if (type == 'update') {
        const { response: { error_message: errorMessage } } = await dispatch(updateEgressProviderMapping({
          type: props.type,
          id: props.source_id.replaceAll('-', ''),
          data: userData
        }))
        notifySuccess({ errorMessage, type: 'update' })
      } else {
        const { response: { error_message: errorMessage } } = await dispatch(createEgressProviderMapping({
          type: props.type,
          id: props.source_id.replaceAll('-', ''),
          data: userData
        }))
        notifySuccess({ errorMessage, type: 'create' })
      }
      dispatch(getMappings())
    } catch (error) {
      notifyFailure({ error, type })
    }
    setModal(false)
  }

  if (egressMappingsLoading) {
    return (<div> <LoadingSpinner /></div>);
  }

  return (
    <React.Fragment>
      <DataTable
        id="egress_mappings"
        data={mappings}
        columns={tableColumns}
        actions={actions}
        onAction={onAction}
        mainId="egress_provider_mapping_id"
        add={{
          name: t('buttons.Add'),
          onClick: () => {setEdit(false); setModal(true)}
        }}
      />
      <Modal
        icon={<FontAwesomeIcon icon={faArrowProgress} />}
        iconBg="tw-bg-blue-500 tw-text-white"
        title="egress_provider.configure-egress"
        contentRaw={
          <Groups noPadding section="egress_provider" className='tw-text-left tw-mt-8' onSubmit={handleSubmit(addMapping)}>
            <p className="tw-pt-4 tw-text-xs" style={{ color: 'var(--text-color-muted-more)' }}>{t("egress_provider.configure-egress-text")}</p>
            <FormField className="tw-flex tw-items-center tw-justify-between">
              <Field type="text"
                name="enabled"
                id="enabled"
                component={renderToggle}
              />
            </FormField>
            <Field type="text"
              name="egress_provider_id"
              id="egress_provider_id"
              component={renderSelectField}
              validate={required} required
            >
              <option value="">Select...</option>
              {egressProviderOptions && egressProviderOptions.map(provider => <option value={provider.egress_provider_id}>{provider.name}</option>)}

            </Field>
            {egress_provider_id && (
              <React.Fragment>
                <FormField className="tw-flex tw-items-center tw-justify-between">
                  <Field type="text"
                    name="allow_all_gateways"
                    help="kb-26-10"
                    id="allow_all_gateways"
                    component={renderToggle}
                  />
                </FormField>

                {!allow_all_gateways && (
                  <Field
                    multi
                    onChange={(details) => setSelectedGateways(details)}
                    selectedValue={selectedGateways}
                    name="selected_gateways"
                    help="kb-26-11"
                    id="selected_gateways"
                    component={SelectInput}
                    options={gateways}>
                  </Field>
                )}
              </React.Fragment>
            )}
            <ModalFooter saving={false} cancel={() => setModal(false)} saveName='buttons.Save' />
          </Groups>
        }
        open={modal}
        setOpen={setModal}
      />

      <ConfirmAction
        confirmationDetails={{
          action: null,
          details: {
            title: t('egress_provider.notify-delete-title-mapping'),
            text: t('egress_provider.are-you-sure-you-want-to-delet-mapping'),
            iconBg: 'tw-bg-pink-700 tw-text-white',
            icon: <FontAwesomeIcon icon={faTrash} />,
            confirmBg: 'tw-bg-pink-700',
            confirmText: t('buttons.Delete'),
          }
        }}
        open={confirmModal}
        externalClose={true}
        setOpen={setConfirmModal}
        onAction={deleteMapping}
      />
    </React.Fragment>
  )
}

EgressMapping = reduxForm({
  form: 'EgressMapping'
})(EgressMapping)

EgressMapping = connect(  // eslint-disable-line
  state => ({
    initialValues: { enabled: true, allow_all_gateways: true },
    egress_provider_id: selector(state, 'egress_provider_id'),
    allow_all_gateways: selector(state, 'allow_all_gateways'),
    selected_gateways: selector(state, 'selected_gateways'),
    egress_provider_mapping_id: selector(state, 'egress_provider_mapping_id'),

  }))
  (EgressMapping);

export default EgressMapping